import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shapes,
  BookOpen,
  FileText,
  Settings,
  Users,
  PlusCircle,
  LayoutDashboard,
  Shield,
  ArrowRight,
  TrendingUp,
  Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isMobileDevice } from '../lib/utils';

export function DashboardHub() {
  const { user, role, memberships, currentOrg, isPlatformAdmin } = useAuth();

  const stats = [
    { label: 'Source Sets', value: 'Ready', icon: FileText, color: 'text-indigo-500' },
    { label: 'Published Learning', value: 'Review', icon: BookOpen, color: 'text-emerald-500' },
    { label: 'Learner Progress', value: 'Track', icon: Users, color: 'text-cyan-500' },
  ];

  const [showSettings, setShowSettings] = React.useState(false);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-bg-main p-4 sm:p-6 lg:p-8">
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-bg-main/80 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-bg-alt border border-border-main rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-text-main mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-500" />
              Account Settings
            </h2>

            <div className="space-y-4 mb-8">
              <div className="bg-bg-main border border-border-main p-4 rounded-xl">
                <p className="text-sm text-text-alt mb-1">Signed in as</p>
                <p className="font-bold text-text-main">{user?.email}</p>
                {user?.displayName && <p className="text-sm text-text-main mt-1">{user.displayName}</p>}
              </div>

              <div className="bg-bg-main border border-border-main p-4 rounded-xl">
                <p className="text-sm text-text-alt mb-1">Current Role</p>
                <p className="font-bold text-text-main capitalize">{role}</p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
              >
                Close Settings
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-text-main flex items-center gap-3">
              <LayoutDashboard className="h-8 w-8 text-indigo-500" />
              Welcome back, {user?.displayName || 'Tutor'}
            </h1>
            <p className="text-text-alt mt-2 text-lg">
              Start with trusted sources, create learning, ask source questions, and manage learner progress from one central hub.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isMobileDevice() ? (
              <button
                disabled
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600/50 text-white/50 rounded-xl font-bold cursor-not-allowed"
                title="Course creation requires a desktop environment."
              >
                <PlusCircle className="h-5 w-5" />
                Desktop Required to Build
              </button>
            ) : (
              <Link
                to="/source-sets"
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
              >
                <PlusCircle className="h-5 w-5" />
                Start with Source Set
              </Link>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {stats.map((stat, i) => (
            <div key={i} className="bg-bg-alt border border-border-main rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-lg bg-bg-main border border-border-main ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
                <TrendingUp className="h-4 w-4 text-emerald-500 opacity-50" />
              </div>
              <div className="text-2xl font-bold text-text-main">{stat.value}</div>
              <div className="text-sm text-text-alt font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Pilot Checklist */}
        <div className="bg-bg-alt/50 border border-indigo-500/30 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row gap-6 items-start relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
            <LayoutDashboard className="w-48 h-48" />
          </div>
          <div className="flex-shrink-0">
            <div className="h-12 w-12 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Shield className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="flex-1 z-10">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-text-main">Source First Workflow</h2>
              <span className="text-[10px] font-bold bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full uppercase tracking-wider border border-indigo-500/30">
                Batch 4B
              </span>
            </div>
            <p className="text-sm text-text-alt mb-4 max-w-2xl">
              Use this workflow to turn trusted documents and URLs into grounded answers, reviewable learning, and measurable understanding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8 text-sm">
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Create a Source Sets set
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Generate a summary or Create Learning
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Review before publish
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Publish privately, publicly, or to an organisation
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Open as learner
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Ask Source questions from the same materials
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Use knowledge checks to verify understanding
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Use Driving Mode for audio review
              </label>
              <label className="flex items-center gap-3 text-text-main cursor-pointer hover:opacity-80">
                <input type="checkbox" className="rounded border-border-main accent-indigo-600 w-4 h-4 bg-bg-main" />
                Send feedback
              </label>
            </div>
          </div>
        </div>

        {/* Dashboard Hub Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-stretch">
          {/* Source Sets Card */}
          <div className="bg-bg-alt border border-indigo-500/40 rounded-3xl p-8 shadow-sm flex flex-col group hover:border-indigo-500 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="h-14 w-14 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest bg-indigo-500/10 text-indigo-400 px-3 py-1 rounded-full border border-indigo-500/20">
                New
              </span>
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Source Sets</h2>
            <p className="text-text-alt leading-relaxed mb-8 flex-1">
              Create a Source Set from documents, URLs, webpages, or blog links. Use it as the foundation for summaries, Create Learning, and Ask Source.
            </p>
            <Link
              to="/source-sets"
              className="mt-auto group/btn inline-flex items-center justify-center gap-2 w-full py-4 bg-indigo-600 border border-indigo-600 rounded-2xl font-bold text-white hover:bg-indigo-700 hover:border-indigo-700 transition-all duration-300"
            >
              Open Source Sets
              <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Organisation Management Card */}
          <div className="bg-bg-alt border border-border-main rounded-3xl p-8 shadow-sm flex flex-col group hover:border-indigo-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="h-14 w-14 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <Shapes className="h-7 w-7 text-white" />
              </div>
              {currentOrg && (
                <span className="text-xs font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full border border-emerald-500/20">
                  Active Org: {currentOrg.name}
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Organisation Dashboard</h2>
            <p className="text-text-alt leading-relaxed mb-8 flex-1">
              Manage team members, roles, organisation visibility, and learning access for your team.
            </p>
            <Link
              to="/organisation"
              className="mt-auto group/btn inline-flex items-center justify-center gap-2 w-full py-4 bg-bg-main border border-border-main rounded-2xl font-bold text-text-main hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all duration-300"
            >
              Enter Org Dashboard
              <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Course Management Card */}
          <div className="bg-bg-alt border border-border-main rounded-3xl p-8 shadow-sm flex flex-col group hover:border-indigo-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="h-14 w-14 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-text-main mb-3">Classic Course Creator</h2>
            <p className="text-text-alt leading-relaxed mb-8 flex-1">
              Use the original single-document course creation flow as a reliable fallback for simple course generation.
            </p>
            {isMobileDevice() ? (
              <button
                disabled
                className="mt-auto group/btn inline-flex items-center justify-center gap-2 w-full py-4 bg-bg-main border border-border-main rounded-2xl font-bold text-text-alt opacity-50 cursor-not-allowed"
              >
                Desktop Required
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : (
              <Link
                to="/courses/manage"
                className="mt-auto group/btn inline-flex items-center justify-center gap-2 w-full py-4 bg-bg-main border border-border-main rounded-2xl font-bold text-text-main hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all duration-300"
              >
                Open Classic Creator
                <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            )}
          </div>

          {/* Admin & Tools Section */}
          <div className="xl:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-bg-alt/50 border border-border-main rounded-2xl p-6 flex items-center gap-5">
                <div className="h-12 w-12 bg-bg-main rounded-xl flex items-center justify-center shrink-0 border border-border-main">
                  <BookOpen className="h-6 w-6 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-text-main">My Documents</h3>
                  <p className="text-sm text-text-alt">View uploaded documents and saved briefs.</p>
                </div>
                <Link to="/documents" className="p-2 hover:bg-emerald-600 hover:text-white rounded-lg transition-colors border border-border-main">
                  <ArrowRight className="h-5 w-5" />
                </Link>
            </div>

            {isPlatformAdmin && (
              <div className="bg-bg-alt/50 border border-border-main rounded-2xl p-6 flex items-center gap-5">
                <div className="h-12 w-12 bg-bg-main rounded-xl flex items-center justify-center shrink-0 border border-border-main">
                  <Shield className="h-6 w-6 text-indigo-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-text-main">System Admin</h3>
                  <p className="text-sm text-text-alt">Administrative logs and system oversight tools.</p>
                </div>
                <Link to="/admin" className="p-2 hover:bg-indigo-600 hover:text-white rounded-lg transition-colors border border-border-main">
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            )}

            <div className="bg-bg-alt/50 border border-border-main rounded-2xl p-6 flex items-center gap-5">
              <div className="h-12 w-12 bg-bg-main rounded-xl flex items-center justify-center shrink-0 border border-border-main">
                <Settings className="h-6 w-6 text-text-alt" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-text-main">Account Settings</h3>
                <p className="text-sm text-text-alt">Manage profile, preferences, and security.</p>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 hover:bg-indigo-600 hover:text-white rounded-lg transition-colors border border-border-main"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer Area */}
        <div className="pt-8 border-t border-border-main flex flex-col md:flex-row justify-between items-center gap-4 text-text-alt text-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Last activity: Recently
          </div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-indigo-500 transition-colors">Source Sets Guide</a>
            <a href="#" className="hover:text-indigo-500 transition-colors">Support</a>
            <a href="#" className="hover:text-indigo-500 transition-colors">Release Notes</a>
          </div>
        </div>
      </div>
    </div>
  );
}
